import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { setAdminToken, isAdminTokenValid, ensureAdminAxiosInterceptor } from '../../utils/adminSession';
import { isApprovedAdmin } from '../../data/approvedAdmins';
import '../../styles/admin.css';

ensureAdminAxiosInterceptor();

function readUser() {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('adeeb-user') : null;
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function readUserEmail() {
  return readUser()?.email || '';
}

export default function AdminLogin() {
  const navigate = useNavigate();
  const [gateChecked, setGateChecked] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [email, setEmail] = useState(readUserEmail);
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [pending, setPending] = useState(false);
  const [err, setErr] = useState('');
  const [info, setInfo] = useState('');
  const otpRef = useRef(null);

  // Gate: only let approved admins (logged in AND on the allowlist) even
  // see this page. Anyone else gets bounced silently to home — no hint
  // that an admin login surface exists. We confirm against /api/auth/me
  // (in case the cached user is stale) before showing the form.
  useEffect(() => {
    let alive = true;
    const cached = readUser();
    if (!cached || !cached.email || !localStorage.getItem('adeeb-token')) {
      navigate('/', { replace: true });
      return () => { alive = false; };
    }
    const token = localStorage.getItem('adeeb-token');
    axios.get('/api/auth/me', { headers: { Authorization: `Bearer ${token}` }, timeout: 8000 })
      .then(({ data }) => {
        if (!alive) return;
        if (!isApprovedAdmin(data?.user)) {
          navigate('/', { replace: true });
          return;
        }
        if (data.user.email && data.user.email !== email) setEmail(data.user.email);
        if (isAdminTokenValid()) navigate('/admin', { replace: true });
        else setGateChecked(true);
      })
      .catch(() => { if (alive) navigate('/', { replace: true }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const submitStep1 = async (e) => {
    e.preventDefault();
    if (pending) return;
    setErr(''); setInfo(''); setPending(true);
    try {
      const { data } = await axios.post('/api/auth/admin/start',
        { email: email.trim(), password },
        { timeout: 12000 }
      );
      setStep('otp');
      setInfo(`Code sent to ${data.email || email}. It expires in ${data.ttlMinutes || 10} minutes.`);
      setPassword('');
      setTimeout(() => otpRef.current?.focus(), 30);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not send a sign-in code.');
    } finally {
      setPending(false);
    }
  };

  const submitStep2 = async (e) => {
    e.preventDefault();
    if (pending) return;
    setErr(''); setInfo(''); setPending(true);
    try {
      const { data } = await axios.post('/api/auth/admin/verify',
        { email: email.trim(), otp: otp.trim() },
        { timeout: 12000 }
      );
      setAdminToken(data.adminToken);
      navigate('/admin', { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not verify the code.');
    } finally {
      setPending(false);
    }
  };

  const resend = async () => {
    if (pending) return;
    setErr(''); setInfo(''); setPending(true);
    try {
      // Reuse step1 — but the user would need to re-enter the password.
      // Instead, tell them to go back if they need a fresh code.
      setStep('credentials');
      setInfo('Re-enter your password to send a new code.');
    } finally { setPending(false); }
  };

  // Render nothing until the gate has confirmed this is an approved admin.
  // Anyone unauthenticated has already been redirected by the effect above.
  if (!gateChecked) {
    return (
      <div className="admin-fullpage">
        <div className="admin-loading">
          <div className="admin-spinner" />
          <p>Checking access…</p>
        </div>
      </div>
    );
  }

  // Render the loader as a portal to <body> so no parent's CSS containing
  // block (transform / filter / backdrop-filter / contain) can pull it
  // off-centre. Always sits dead-centre of the viewport.
  const loaderNode = pending && typeof document !== 'undefined'
    ? createPortal(
        <div className="admin-login-loader" role="status" aria-live="polite">
          <div className="admin-login-loader-inner">
            <div className="admin-spinner" />
            <span className="admin-login-loader-label">
              {step === 'credentials' ? 'Sending code…' : 'Verifying…'}
            </span>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="admin-login-wrapper">
      {loaderNode}
      <div className={`admin-login-card ${pending ? 'is-pending' : ''}`}>
        <div className="admin-login-brand">
          <img className="admin-brand-image" src="/branding/deenlit-logo.png" alt="DeenLit" width="2172" height="724" />
          <span className="admin-login-sub">Admin Studio</span>
        </div>

        <h1 className="admin-login-title">
          {step === 'credentials' ? 'Sign in to admin' : 'Enter your sign-in code'}
        </h1>
        <p className="admin-login-hint">
          {step === 'credentials'
            ? 'For your security, please confirm your password — we’ll email a one-time code next.'
            : 'We just emailed you a 6-digit code. Enter it to unlock the studio.'}
        </p>

        {err && <div className="admin-login-alert is-error">{err}</div>}
        {info && <div className="admin-login-alert">{info}</div>}

        {step === 'credentials' ? (
          <form className="admin-login-form" onSubmit={submitStep1} noValidate>
            <label className="admin-login-field">
              <span className="admin-login-label">Email</span>
              <input
                className="admin-input"
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
              />
            </label>
            <label className="admin-login-field">
              <span className="admin-login-label">Password</span>
              <input
                className="admin-input"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </label>
            <button type="submit" className="admin-btn admin-login-submit" disabled={pending}>
              {pending ? 'Sending code…' : 'Send sign-in code'}
            </button>
            <Link to="/" className="admin-login-back">← Back to site</Link>
          </form>
        ) : (
          <form className="admin-login-form" onSubmit={submitStep2} noValidate>
            <label className="admin-login-field">
              <span className="admin-login-label">6-digit code</span>
              <input
                ref={otpRef}
                className="admin-input admin-login-otp"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
                placeholder="••••••"
              />
            </label>
            <button type="submit" className="admin-btn admin-login-submit" disabled={pending || otp.length !== 6}>
              {pending ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button type="button" className="admin-login-back" onClick={resend} disabled={pending}>
              Didn’t get it? Send a new code
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
